/**
 * useWritingHistory hook
 *
 * Implements show-not-tell/TS-014 (T4, AC-TS014.1, AC-TS014.3). Pages through
 * GET /sessions/show-not-tell/history using React Query's useInfiniteQuery,
 * keyed ['writing','history'] — deliberately distinct from the dashboard
 * tile's ['writing','recent'] queryKey (WritingPracticeTile.tsx) so the two
 * surfaces cache independently and never thrash each other.
 *
 * Mirrors the useSessions data-hook return-shape convention (items,
 * isLoading, error/isError, refetch) with the additional paging controls
 * (fetchNextPage, hasNextPage, isFetchingNextPage) useInfiniteQuery provides.
 * pageParam is the opaque nextCursor from the previous page (TS-014 decision
 * log DD-1's keyset cursor) — getNextPageParam stops paging once nextCursor
 * is null/absent.
 *
 * Errors are surfaced via isError (never swallowed) so
 * WritingHistoryPage (T6) can render the retry-able ErrorState per DD-3 —
 * a history read failure must never silently degrade to an empty list.
 *
 * Modified by BUG-20260718-154634 (C-2/C-3): additionally exposes
 * `activeSession`, sourced from a SEPARATE GET /sessions/show-not-tell/active
 * fetch (queryKey ['writing','active']) — not merged into the paginated
 * /history response, which is a PINNED contract with an existing exact-
 * match test (backend/tests/show-not-tell-TS-014/test_snt_history.py).
 * Together the two reads let the landing page render a resume-vs-start
 * affordance, mirroring comprehension's useSessions()
 * (frontend/src/hooks/useSessions.ts, read-only pattern reference)
 * returning both active + completed — here via two small, independently-
 * cacheable queries instead of one, to avoid touching /history's contract.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getWritingHistory, getActiveWritingSession } from '@/services/showNotTell';
import type { RecentWritingSession, ActiveWritingSessionSummary } from '@/types/writingPractice';

export interface UseWritingHistoryReturn {
  items: RecentWritingSession[];
  /** BUG-20260718-154634 (C-3): the caller's own active session, or null. */
  activeSession: ActiveWritingSessionSummary | null;
  isLoading: boolean;
  isError: boolean;
  /**
   * BUG-20260718-172622 (T-4): true when the /active read itself FAILED
   * (network/HTTP error), as distinct from a genuine `activeSession: null`
   * result. Additive — does not affect `isError` (still history-only) or
   * `activeSession` (still degrades to null on error, for callers that
   * don't check activeError). See WritingHistoryPage for the consumer.
   */
  activeError: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  refetch: () => void;
  /** BUG-20260718-172622 (T-4): retry just the /active read. */
  refetchActive: () => void;
}

export function useWritingHistory(): UseWritingHistoryReturn {
  const query = useInfiniteQuery({
    queryKey: ['writing', 'history'],
    queryFn: ({ pageParam }) => getWritingHistory(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // BUG-20260718-154634 (C-3): a SEPARATE, independently-cached read — see
  // module docstring above for why this is not merged into the /history
  // useInfiniteQuery. `activeSession` still degrades to null on error (a
  // resume-hint failure must never block the primary history list from
  // rendering), but BUG-20260718-172622 (T-4) additionally surfaces the
  // query's own error state via `activeError` — a genuine /active failure
  // (e.g. an infra routing 404, not just a "no active session" null) is no
  // longer silently indistinguishable from a legitimate empty result; see
  // getActiveWritingSession's docstring for why the old "backend always
  // fails-safe" assumption no longer holds.
  const activeQuery = useQuery({
    queryKey: ['writing', 'active'],
    queryFn: () => getActiveWritingSession(),
  });

  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const activeSession = activeQuery.data?.activeSession ?? null;

  return {
    items,
    activeSession,
    // BUG-20260718-154634 (follow-up): the landing's start-vs-resume decision
    // is driven by `activeSession`, so it MUST wait for the active-session
    // query too — not just the history query. Reporting only query.isLoading
    // let the page commit to "Start a new session" the instant /history
    // resolved, while /active was still in flight, so a student WITH an active
    // session briefly (or, if the re-render settled, persistently) saw the
    // Start affordance instead of Resume. Gate on BOTH.
    isLoading: query.isLoading || activeQuery.isLoading,
    isError: query.isError,
    // BUG-20260718-172622 (T-4): additive — history's isError stays
    // history-only (the list must never be blocked by a resume-hint
    // failure); activeError lets the page distinguish an errored /active
    // read from a genuine null in its own hero-slot branch.
    activeError: activeQuery.isError,
    fetchNextPage: () => {
      query.fetchNextPage();
    },
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    refetch: () => {
      query.refetch();
    },
    refetchActive: () => {
      activeQuery.refetch();
    },
  };
}
